import {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {sendRequest} from 'api/api'
import {
  getUrlMonthlyRatingCancel,
  getUrlMonthlyRatingCheck,
  getUrlMonthlyRatingCreate,
} from 'api/url'
import toast from 'Component/Toast'
import {DISPLAY_NAME_MENU} from 'const/display_name_menu'
import {UposLogFunc} from 'util/functionUtil'
import {
  MONTHLY_REVIEW_CONTENT_MAX_LENGTH,
  MONTHLY_REVIEW_DEFAULT_STAR,
  MONTHLY_REVIEW_MAX_STAR,
  MONTHLY_REVIEW_MIN_STAR,
  MONTHLY_REVIEW_START_DAY,
} from '../component/monthlyReview/_constants'

const getCurrentMonth = () => new Date().getMonth() + 1

// /check: success = true -> tài khoản CHƯA xử lý kỳ này -> hiển thị popup.
// success = false -> đã Gửi đánh giá HOẶC đã Bỏ qua (ở bất kỳ môi trường nào)
// -> không hiển thị popup nữa.
const shouldShowReview = async () => {
  const res = await sendRequest('get', getUrlMonthlyRatingCheck())
  return !!res?.data?.success
}

export const useMonthlyReview = () => {
  const {t} = useTranslation()

  const [open, setOpen] = useState(false)
  const [openThankYou, setOpenThankYou] = useState(false)
  const [loading, setLoading] = useState(false)
  const [star, setStar] = useState(MONTHLY_REVIEW_DEFAULT_STAR)
  const [content, setContent] = useState('')

  // Tháng hiện tại (dương lịch) cho tiêu đề popup - /check không trả về tháng
  const month = getCurrentMonth()

  const requestingRef = useRef(false)
  const fetchedRef = useRef(false)

  const fetchStatus = async () => {
    try {
      if (await shouldShowReview()) setOpen(true)
    } catch (e) {
      UposLogFunc(`ERROR CHECK MONTHLY RATING: ${e?.message}`)
    }
  }

  useEffect(() => {
    if (fetchedRef.current) return
    if (new Date().getDate() < MONTHLY_REVIEW_START_DAY) return
    fetchedRef.current = true
    fetchStatus()
  }, [])

  const finishRequest = () => {
    requestingRef.current = false
    setLoading(false)
  }

  const handleChangeStar = value => {
    if (requestingRef.current) return
    const next = Math.min(
      Math.max(+value || MONTHLY_REVIEW_MIN_STAR, MONTHLY_REVIEW_MIN_STAR),
      MONTHLY_REVIEW_MAX_STAR,
    )
    setStar(next)
  }

  const handleChangeContent = e =>
    setContent(
      (e?.target?.value || '').slice(0, MONTHLY_REVIEW_CONTENT_MAX_LENGTH),
    )

  const handleClose = () => {
    if (requestingRef.current) return
    setOpen(false)
  }

  const handleCloseThankYou = () => setOpenThankYou(false)

  const notifyFailure = (res, logPrefix) => {
    const message = res?.data?.message
    if (message) UposLogFunc(`${logPrefix}: ${message}`)
    toast.error({
      title: message
        ? t(DISPLAY_NAME_MENU.MONTHLY_REVIEW.ALREADY_SENT, {month})
        : t(DISPLAY_NAME_MENU.MONTHLY_REVIEW.SYSTEM_BUSY),
    })
  }

  const handleSkip = async () => {
    if (requestingRef.current) return
    requestingRef.current = true
    setLoading(true)

    let res
    try {
      res = await sendRequest('post', getUrlMonthlyRatingCancel())
    } catch (e) {
      UposLogFunc(`ERROR CANCEL MONTHLY RATING: ${e?.message}`)
    }

    finishRequest()
    setOpen(false)

    if (res?.data?.success) return
    notifyFailure(res, 'CANCEL MONTHLY RATING FAILED')
  }

  const handleSubmit = async () => {
    if (requestingRef.current) return
    requestingRef.current = true
    setLoading(true)

    let res
    try {
      res = await sendRequest('post', getUrlMonthlyRatingCreate(), {
        star,
        content: content.trim(),
      })
    } catch (e) {
      UposLogFunc(`ERROR CREATE MONTHLY RATING: ${e?.message}`)
    }

    if (res?.data?.success) {
      finishRequest()
      setOpen(false)
      setOpenThankYou(true)
      return
    }

    finishRequest()
    setOpen(false)
    notifyFailure(res, 'CREATE MONTHLY RATING FAILED')
  }

  return {
    data: {
      content,
      loading,
      month,
      open,
      openThankYou,
      star,
    },
    functions: {
      handleChangeContent,
      handleChangeStar,
      handleClose,
      handleCloseThankYou,
      handleSkip,
      handleSubmit,
    },
  }
}

export default useMonthlyReview
